import { getPageData, getPages, getLayoutData } from '../api/api';
import { useCallback, useMemo, useRef } from 'react';
import { capitalize, getPropSafely, setCssVars } from '@repo/utils';
import { duration } from '@repo/utils/gsap';
import { useRouter } from 'next/router';
import { DIGITAL_FRAGMENTS, DIGITAL_TYPES } from '@/lib';
import { FRAGMENTS } from '@repo/ui/lib';
import dynamic from 'next/dynamic';
import ExtendedContent from '@/components/common/ExtendedContent';
import ProjectBanner from '@repo/ui/src/components/Banners/ProjectBanner';
import { useGlobals } from '@repo/ui/src/hooks/useGlobal';
import { useScrollContext } from '@repo/ui/src/components/Scroll';
import dynamicWithLoader from '@repo/ui/src/components/common/DynamicWithLoader';
import { useExtendedTheme } from '@repo/ui/src/components/Theme';
import { usePageTransitionContext } from '@repo/ui/src/components/PageTransition';

const StickyHeading = dynamic(() => import('@repo/ui/src/components/StickyHeading'), {
	ssr: false,
});

const NextProject = dynamicWithLoader(() => import('@repo/ui/src/components/NextProject'));

const SingleProjectElevatorNav = dynamic(() => import('@repo/ui/src/components/SingleProjectElevatorNav'), {
	ssr: false,
});

export default function Page({ data, locale }) {
	const { name, description, nextProject, projectNavigationElevator, content, themeLight } = data;
	const { projectElevatorText } = useGlobals((state) => ({
		projectElevatorText: getPropSafely(state, 'projectElevatorText'),
	}));
	const hasUserScrolled = useScrollContext((s) => s.hasUserScrolled);

	const NextProjectRef = useRef();
	const extendedTheme = useExtendedTheme();
	const router = useRouter();
	const scroll = useScrollContext();
	const { isProjectToProjectOutsideMenuRouteChangeState } = usePageTransitionContext();
	const bannerData = {
		name,
		description,
	};
	const bgColor = themeLight?.mainPrimary?.hex || 'rgba(238, 239, 241, 1)';
	const textColor = themeLight?.mainSecondary?.hex || 'rgb(28,28,28)';
	const nextProjectProps = useMemo(() => {
		const mode = capitalize(extendedTheme.mode);
		const bgColor = nextProject?.projectLink?.link?.[`theme${mode}`]?.mainPrimary?.hex || 'lightGray';
		const textColor = nextProject?.projectLink?.link?.[`theme${mode}`]?.mainSecondary?.hex || 'black';

		return {
			data: {
				tag: nextProject?.tag || '',
				name: nextProject?.projectLink?.link?.name || '',
				description: nextProject?.projectLink?.link?.description || '',
				video: nextProject?.projectLink?.link?.video || {},
				button: {
					text: nextProject?.projectLink?.text || '',
					variant: nextProject?.projectLink?.variant || 'primary',
					slug: nextProject?.projectLink?.link?.slug || '',
				},
			},
			bgColor,
			textColor,
		};
	}, [nextProject, extendedTheme.mode]);

	const onButtonClick = useCallback(
		(slug) => {
			setCssVars(document.body, { 'project-color': nextProjectProps.bgColor });
			router.push(slug).then(() => {
				setCssVars(document.body, { 'project-color': 'unset' });
			});
		},
		[scroll, nextProjectProps],
	);

	const projectNavigationElevatorData =
		projectNavigationElevator &&
		projectNavigationElevator.map((el) => {
			return {
				anchor: el?.link?.id || '',
				id: el?.link?.id || '',
				text: el?.elevatorText || '',
				afterPin: el?.afterPin || false,
			};
		});

	return (
		<>
			{projectNavigationElevatorData?.length > 0 && hasUserScrolled && (
				<SingleProjectElevatorNav
					data={projectNavigationElevatorData}
					locale={locale}
					projectElevatorText={projectElevatorText}
					nextProjectProps={nextProjectProps}
					onButtonClick={onButtonClick}
				/>
			)}
			<StickyHeading stickyHeadingsSelectors={[]} className="hidden" isVisible={false} />
			<ProjectBanner data={bannerData} animate={!isProjectToProjectOutsideMenuRouteChangeState} />
			<ExtendedContent content={content} locale={locale} bgColor={bgColor} textColor={textColor} disableProjectRange={true} />
			<NextProject
				key={router.asPath}
				ref={NextProjectRef}
				onButtonClickStart={() => {
					scroll.instance.scrollTo(NextProjectRef.current, { duration, lock: true });
				}}
				onButtonClickComplete={onButtonClick}
				{...nextProjectProps}
			/>
		</>
	);
}

const PAGE_CONTENT_TYPE = 'allDigitalSingleProjects';

const pathsQuery = `
query GetSingleProject($locale: SiteLocale) {
	${PAGE_CONTENT_TYPE}(locale: $locale, first: 100) {
	  name
	  slug
	}
}
`;

const propsQuery = `
	query GetProject($slug: String, $locale: SiteLocale) {
		${PAGE_CONTENT_TYPE}(locale: $locale, filter: { slug: { eq: $slug } }) {
			name
			slug
			highlight
			${FRAGMENTS.SEO_META_FRAGMENT}
			projectColor {
				hex
			}
			projectServiceRange {
				serviceName
			}
			projectCategories {
				projectCategory
				projectCategoryId
			}
			description
			bannerWhiteLogo
			bannerWhiteMenu
			year
			${FRAGMENTS.THEME_FRAGMENT}
			coverPortrait {
				${FRAGMENTS.IMAGE_FRAGMENT}
			}
			coverLandscape {
				${FRAGMENTS.IMAGE_FRAGMENT}
			}
			${DIGITAL_FRAGMENTS.BREADCRUMBS_FRAGMENT}
			${DIGITAL_FRAGMENTS.NAVIGATION_ELEVATOR_FRAGMENT}
			content {
				__typename
				
				${DIGITAL_FRAGMENTS.TILES_MODULE_FRAGMENT}
				${DIGITAL_FRAGMENTS.HEADER_AND_DESCRIPTION_FRAGMENT}
				${DIGITAL_FRAGMENTS.CHECK_OTHERS_FRAGMENT}
				${DIGITAL_FRAGMENTS.SINGLE_ASSET_FRAGMENT}
				
				${FRAGMENTS.PADDING_MODULE_FRAGMENT}
				${FRAGMENTS.ASSETS_GALLERY_FRAGMENT}
				${FRAGMENTS.ASSET_WITH_HEADER_FRAGMENT}
				${FRAGMENTS.PROJECT_SECTION_HEADER_FRAGMENT}
				${FRAGMENTS.PROJECT_RANGE_FRAGMENT}
				${FRAGMENTS.PROJECT_SCOPE_FRAGMENT}
				${FRAGMENTS.TEXT_MODULE_FRAGMENT}
				${FRAGMENTS.PINNED_TEXT_WITH_IMAGE_FRAGMENT}
                ${FRAGMENTS.PROJECT_SLIDER_FRAGMENT}
                ${FRAGMENTS.TECHNOLOGIES_TABLE_FRAGMENT}
			}
			nextProject {
			  id
			  tag
			  projectLink {
				text
				variant
				link {
				  ... on ${DIGITAL_TYPES.PAGES.SINGLE_PROJECT} {
					name
					slug
					description
					video {
						${FRAGMENTS.VIDEO_FRAGMENT}
					}
					${FRAGMENTS.THEME_FRAGMENT}
				  }
				}
			  }
			}
		}
	}
`;

export async function getStaticProps({ params, locale }) {
	const { slug } = params;
	const data = await getPageData(PAGE_CONTENT_TYPE, slug, propsQuery, locale);
	const { globals, cookies, menu, footer } = await getLayoutData(locale);
	return {
		props: {
			data,
			globals,
			cookies,
			locale,
			menu,
			footer,
		},
		revalidate: 120,
	};
}

export async function getStaticPaths({ locales }) {
	const paths = [];
	for (const locale of locales) {
		const projectPosts = await getPages(PAGE_CONTENT_TYPE, pathsQuery, locale);
		const posts = projectPosts.map((page) => ({
			params: {
				slug: page.slug,
			},
			locale,
		}));
		paths.push(...posts);
	}
	// { fallback: false } means other routes should 404.
	return {
		paths,
		fallback: false,
	};
}
